export const intervalsOverlap = (a, b) =>
	a.startMin < b.endMin && b.startMin < a.endMin;

export const clipInterval = (item, period) => {
	const startMin = Math.max(item.startMin, period.startMin);
	const endMin = Math.min(item.endMin, period.endMin);
	return startMin < endMin ? { startMin, endMin } : null;
};

export const minuteToOffset = (minute, axis, pixelsPerMinute = 1) =>
	(minute - axis.startMin) * pixelsPerMinute;

export const intervalStyle = (startMin, endMin, axis, pixelsPerMinute = 1) => ({
	top: minuteToOffset(startMin, axis, pixelsPerMinute),
	height: (endMin - startMin) * pixelsPerMinute,
});

export const GRID_SLOT_HEIGHT_PX = 52;

const gridPixelsPerMinute = (granularityMinutes) =>
	GRID_SLOT_HEIGHT_PX / granularityMinutes;

export const gridRowStyle = (row, granularityMinutes) => ({
	height: (row.endMin - row.startMin) * gridPixelsPerMinute(granularityMinutes),
});

export const gridItemStyle = (item, axis, granularityMinutes) => {
	const ppm = gridPixelsPerMinute(granularityMinutes);
	return {
		top: minuteToOffset(item.startMin, axis, ppm),
		height: (item.endMin - item.startMin) * ppm,
	};
};

const MAX_AXIS_MINUTE = 2880;
const MAX_GRANULARITY_MINUTES = 240;
const MAX_GRID_ROWS = 192;

const isValidGranularity = (value) =>
	Number.isInteger(value) && value >= 1 && value <= MAX_GRANULARITY_MINUTES;

const hasValidPeriods = (periods) =>
	Array.isArray(periods) &&
	periods.length > 0 &&
	periods.every((period, index) => {
		const validInterval =
			Number.isInteger(period?.startMin) &&
			Number.isInteger(period?.endMin) &&
			period.startMin >= 0 &&
			period.endMin > period.startMin &&
			period.endMin <= MAX_AXIS_MINUTE;
		return (
			validInterval &&
			(index === 0 || periods[index - 1].endMin <= period.startMin)
		);
	});

const rowCount = (periods, granularityMinutes) =>
	periods.reduce(
		(total, period) =>
			total + Math.ceil((period.endMin - period.startMin) / granularityMinutes),
		0,
	);

const isObjectEntry = (entry) =>
	entry !== null && typeof entry === "object" && !Array.isArray(entry);

const hasDisplayId = (entry) =>
	typeof entry.id === "string" && entry.id.trim() !== "";

const hasValidInterval = (entry) =>
	Number.isInteger(entry.startMin) &&
	Number.isInteger(entry.endMin) &&
	entry.startMin >= 0 &&
	entry.endMin > entry.startMin &&
	entry.endMin <= MAX_AXIS_MINUTE;

const isValidReservationEntry = (reservation, courtIds) =>
	isObjectEntry(reservation) &&
	hasDisplayId(reservation) &&
	courtIds.has(reservation.courtId) &&
	hasValidInterval(reservation) &&
	typeof reservation.status === "string" &&
	reservation.status.trim() !== "" &&
	typeof reservation.occupies === "boolean";

const isValidBlockEntry = (block, courtIds) => {
	if (
		!isObjectEntry(block) ||
		!hasDisplayId(block) ||
		!["court", "all-courts"].includes(block.scope) ||
		typeof block.allDay !== "boolean"
	) {
		return false;
	}
	if (block.scope === "court" && !courtIds.has(block.courtId)) return false;
	if (block.scope === "all-courts" && block.courtId !== null) return false;
	return block.allDay || hasValidInterval(block);
};

export const validateReadySchedule = (schedule) => {
	if (schedule?.state !== "ready" || schedule.bookable !== true) {
		return { valid: false, reason: "not_ready" };
	}
	if (
		!Array.isArray(schedule.courts) ||
		schedule.courts.length === 0 ||
		schedule.courts.some(
			(court) =>
				!court || typeof court.id !== "string" || court.id.trim() === "",
		) ||
		new Set(schedule.courts.map((court) => court.id)).size !==
			schedule.courts.length
	) {
		return { valid: false, reason: "invalid_courts" };
	}
	if (
		(schedule.reservations !== undefined &&
			!Array.isArray(schedule.reservations)) ||
		(schedule.blocks !== undefined && !Array.isArray(schedule.blocks))
	) {
		return { valid: false, reason: "invalid_items" };
	}
	const courtIds = new Set(schedule.courts.map((court) => court.id));
	if (
		(schedule.reservations || []).some(
			(entry) => !isValidReservationEntry(entry, courtIds),
		) ||
		(schedule.blocks || []).some((entry) => !isValidBlockEntry(entry, courtIds))
	) {
		return { valid: false, reason: "invalid_items" };
	}
	if (!isValidGranularity(schedule.granularityMinutes)) {
		return { valid: false, reason: "invalid_granularity" };
	}
	const { axis } = schedule;
	if (
		!axis ||
		!Number.isInteger(axis.startMin) ||
		!Number.isInteger(axis.endMin) ||
		axis.startMin < 0 ||
		axis.endMin <= axis.startMin ||
		axis.endMin > MAX_AXIS_MINUTE ||
		!hasValidPeriods(axis.periods) ||
		axis.startMin > axis.periods[0].startMin ||
		axis.endMin < axis.periods[axis.periods.length - 1].endMin ||
		rowCount(axis.periods, schedule.granularityMinutes) > MAX_GRID_ROWS
	) {
		return { valid: false, reason: "invalid_axis" };
	}
	return { valid: true, reason: null };
};

export const buildRows = (periods = [], granularityMinutes) => {
	if (
		!isValidGranularity(granularityMinutes) ||
		!hasValidPeriods(periods) ||
		rowCount(periods, granularityMinutes) > MAX_GRID_ROWS
	) {
		return [];
	}

	const rows = [];
	periods.forEach((period, index) => {
		if (index > 0 && periods[index - 1].endMin < period.startMin) {
			rows.push({
				type: "gap",
				startMin: periods[index - 1].endMin,
				endMin: period.startMin,
			});
		}
		for (
			let startMin = period.startMin;
			startMin < period.endMin;
			startMin += granularityMinutes
		) {
			rows.push({
				type: "row",
				startMin,
				endMin: Math.min(startMin + granularityMinutes, period.endMin),
			});
		}
	});
	return rows;
};

export const formatAxisMinute = (minute) => {
	const day = Math.floor(minute / 1440);
	const normalized = ((minute % 1440) + 1440) % 1440;
	const time = `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(normalized % 60).padStart(2, "0")}`;
	if (day <= 0) return time;
	return `${time} (+${day} ${day === 1 ? "día" : "días"})`;
};

export const expandBlocks = (rawBlocks = [], courts = [], periods = []) =>
	rawBlocks.flatMap((block) => {
		const targetCourts =
			block.scope === "all-courts"
				? courts
				: courts.filter((court) => String(court.id) === String(block.courtId));
		const ranges = block.allDay ? periods : [block];
		return targetCourts.flatMap((court) =>
			ranges.flatMap((range) => {
				const clippedRanges = periods
					.map((period) => clipInterval(range, period))
					.filter(Boolean);
				return clippedRanges.map((clipped) => ({
					...block,
					courtId: court.id,
					startMin: clipped.startMin,
					endMin: clipped.endMin,
					kind: "block",
				}));
			}),
		);
	});

export const buildCourtLayers = (schedule, showCancelled = false) => {
	const periods = schedule?.axis?.periods || [];
	const courts = Array.isArray(schedule?.courts) ? schedule.courts : [];
	const reservations = Array.isArray(schedule?.reservations)
		? schedule.reservations
		: [];
	const blocks = Array.isArray(schedule?.blocks) ? schedule.blocks : [];
	const courtIds = new Set(courts.map((court) => court.id));
	const malformedReservation = reservations.some(
		(reservation) => !isValidReservationEntry(reservation, courtIds),
	);
	const malformedBlock = blocks.some(
		(block) => !isValidBlockEntry(block, courtIds),
	);
	const validBlocks = malformedBlock ? [] : blocks;
	const blockPlacements = expandBlocks(validBlocks, courts, periods);
	const layers = Object.fromEntries(
		courts.map((court) => [
			court.id,
			{ occupancy: [], history: [], hitItems: [] },
		]),
	);
	const hasUnknownOccupancy = malformedReservation || malformedBlock;

	if (!malformedReservation) {
		reservations.forEach((reservation) => {
			const layer = layers[reservation.courtId];
			if (reservation.occupies)
				layer.occupancy.push({ ...reservation, kind: "reservation" });
			else if (showCancelled)
				layer.history.push({ ...reservation, kind: "reservation" });
		});
	}
	blockPlacements.forEach((block) =>
		layers[block.courtId]?.occupancy.push(block),
	);

	Object.values(layers).forEach((layer) => {
		const foreground = [...layer.occupancy].sort((a, b) =>
			a.kind === b.kind ? 0 : a.kind === "block" ? -1 : 1,
		);
		layer.hitItems = [...foreground, ...layer.history];
	});
	return { layers, hasUnknownOccupancy };
};

export const isFreeStart = (
	courtId,
	startMin,
	rowEnd,
	periods = [],
	layerResult,
) => {
	const insideOpenPeriod = periods.some(
		(period) =>
			startMin >= period.startMin &&
			startMin < period.endMin &&
			rowEnd <= period.endMin,
	);
	const layer = layerResult?.layers?.[courtId];
	if (!insideOpenPeriod || !layer || layerResult.hasUnknownOccupancy)
		return false;
	const segment = { startMin, endMin: rowEnd };
	return !layer.occupancy.some((item) => intervalsOverlap(item, segment));
};

export const isCurrentFreeSelection = (schedule, selection) => {
	if (
		!validateReadySchedule(schedule).valid ||
		!selection ||
		selection.date !== schedule.date ||
		!Number.isInteger(selection.startMin) ||
		!Number.isInteger(selection.endMin) ||
		!schedule.courts.some((court) => court.id === selection.courtId)
	) {
		return false;
	}
	const layers = buildCourtLayers(schedule, false);
	return isFreeStart(
		selection.courtId,
		selection.startMin,
		selection.endMin,
		schedule.axis.periods,
		layers,
	);
};
